import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AuthModal from './AuthModal';
import './Header.css';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [isRegisterOpen, setIsRegisterOpen] = useState(false);
  const [isAtTop, setIsAtTop] = useState(true);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsAtTop(scrollPosition < 50);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check initial position

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleClick = (sectionId: string) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        scrollToSection(sectionId);
      }, 300);
    } else {
      scrollToSection(sectionId);
    }
    setIsMenuOpen(false);
  };

  const scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (section) {
      const headerHeight = 80;
      const sectionTop = section.offsetTop - headerHeight;

      window.scrollTo({
        top: sectionTop,
        behavior: 'smooth',
      });

      setActiveSection(sectionId);
    }
  };

  const scrollToTop = () => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <>
      <header className="header">
          <div
            className={`
              header__container 
              transition-all 
              duration-300 
              ${isAtTop 
                ? 'bg-transparent backdrop-blur-none border-transparent'
                : 'bg-black/70 backdrop-blur-md border-b border-black/20'
              }
            `}
          >
          <div className="header__content">
            <button
              onClick={scrollToTop}
              className="header__logo group"
              aria-label="Retour en haut de la page"
            >
            <img
              src="/logo.png"
              alt="EvolTrade Logo"
              className="
                w-[40%]        /* ou w-auto si tu veux que l'image se redimensionne librement */
                max-w-[200px]  /* limite la largeur max */
                object-contain
                transform group-hover:scale-110
                transition-transform duration-300
              "
            />


            </button>

            <nav className="header__nav">
              <div className="header__nav-links">
                <button
                  className={`header__link ${activeSection === 'services' ? 'text-purple-400' : ''}`}
                  onClick={() => handleClick('services')}
                >
                  Services
                </button>
                <button
                  className={`header__link ${activeSection === 'reviews' ? 'text-purple-400' : ''}`}
                  onClick={() => handleClick('reviews')}
                >
                  Avis
                </button>
                <button
                  className={`header__link ${activeSection === 'faq' ? 'text-purple-400' : ''}`}
                  onClick={() => handleClick('faq')}
                >
                  FAQ
                </button>
                <button
                  className="header__button"
                  onClick={() => setIsRegisterOpen(true)}
                >
                  Inscription
                </button>
              </div>
            </nav>

            <button
              className="header__mobile-toggle"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-expanded={isMenuOpen}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-95 backdrop-blur-sm z-40"
            onClick={() => setIsMenuOpen(false)}
          >
            <div className="flex flex-col items-center justify-center h-full space-y-8">
              <button
                className={`text-white text-xl hover:text-purple-400 transition-colors ${
                  activeSection === 'services' ? 'text-purple-400' : ''
                }`}
                onClick={() => handleClick('services')}
              >
                Services
              </button>
              <button
                className={`text-white text-xl hover:text-purple-400 transition-colors ${
                  activeSection === 'reviews' ? 'text-purple-400' : ''
                }`}
                onClick={() => handleClick('reviews')}
              >
                Avis
              </button>
              <button
                className={`text-white text-xl hover:text-purple-400 transition-colors ${
                  activeSection === 'faq' ? 'text-purple-400' : ''
                }`}
                onClick={() => handleClick('faq')}
              >
                FAQ
              </button>
              {/* Dev Link in Mobile Menu */}
              <Link
                to="/welcome"
                className="text-purple-400 text-xl border border-purple-400 px-4 py-2 rounded"
                onClick={() => setIsMenuOpen(false)}
              >
                Welcome Page
              </Link>
              <button
                className="px-8 py-3 bg-gradient-to-r from-purple-600 to-purple-800 text-white rounded-full hover:from-purple-700 hover:to-purple-900 transition-all transform hover:scale-105"
                onClick={() => {
                  setIsMenuOpen(false);
                  setIsRegisterOpen(true);
                }}
              >
                Inscription
              </button>
            </div>
          </div>
        )}
      </header>

      <AuthModal
        isOpen={isRegisterOpen}
        onClose={() => setIsRegisterOpen(false)}
        type="register"
      />
    </>
  );
}